import { useApi } from "./api";

export const useVehicleSettingsService = () => {
  const api = useApi();

  return {
    // ===== Puntos Críticos =====
    getCriticalPoints: async (params = {}) => {
      try {
        const {
          page = 1,
          limit = 10,
          search = "",
          includeInactive = false,
        } = params;
        const response = await api.get("/vehicle-settings/critical-points", {
          params: {
            page,
            limit,
            search,
            include_inactive: includeInactive,
          },
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo puntos críticos:", error);
        throw error.response?.data || error.message;
      }
    },

    getCriticalPointById: async (id) => {
      try {
        const response = await api.get(
          `/vehicle-settings/critical-points/${id}`
        );
        return response.data;
      } catch (error) {
        console.error(`Error obteniendo punto crítico ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    createCriticalPoint: async (pointData) => {
      try {
        const response = await api.post(
          "/vehicle-settings/critical-points",
          pointData
        );
        return response.data;
      } catch (error) {
        console.error("Error creando punto crítico:", error);
        throw error.response?.data || error.message;
      }
    },

    updateCriticalPoint: async (id, pointData) => {
      try {
        const response = await api.put(
          `/vehicle-settings/critical-points/${id}`,
          pointData
        );
        return response.data;
      } catch (error) {
        console.error(`Error actualizando punto crítico ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    deleteCriticalPoint: async (id) => {
      try {
        const response = await api.delete(
          `/vehicle-settings/critical-points/${id}`
        );
        return response.data;
      } catch (error) {
        console.error(`Error eliminando punto crítico ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    toggleCriticalPointStatus: async (id, isActive) => {
      try {
        const response = await api.patch(
          `/vehicle-settings/critical-points/${id}/status`,
          {
            is_active: isActive,
          }
        );
        return response.data;
      } catch (error) {
        console.error(`Error cambiando estado de punto crítico ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    // ===== Rutas Predefinidas =====
    getRoutes: async (params = {}) => {
      try {
        const {
          page = 1,
          limit = 10,
          search = "",
          client = "",
          includeInactive = false,
        } = params;
        const response = await api.get("/vehicle-settings/routes", {
          params: {
            page,
            limit,
            search,
            client,
            include_inactive: includeInactive,
          },
        });
        return response.data;
      } catch (error) {
        console.error("Error obteniendo rutas predefinidas:", error);
        throw error.response?.data || error.message;
      }
    },

    getRouteById: async (id) => {
      try {
        const response = await api.get(`/vehicle-settings/routes/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error obteniendo ruta predefinida ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    createRoute: async (routeData) => {
      try {
        const response = await api.post("/vehicle-settings/routes", routeData);
        return response.data;
      } catch (error) {
        console.error("Error creando ruta predefinida:", error);
        throw error.response?.data || error.message;
      }
    },

    updateRoute: async (id, routeData) => {
      try {
        const response = await api.put(
          `/vehicle-settings/routes/${id}`,
          routeData
        );
        return response.data;
      } catch (error) {
        console.error(`Error actualizando ruta predefinida ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    deleteRoute: async (id) => {
      try {
        const response = await api.delete(`/vehicle-settings/routes/${id}`);
        return response.data;
      } catch (error) {
        console.error(`Error eliminando ruta predefinida ${id}:`, error);
        throw error.response?.data || error.message;
      }
    },

    toggleRouteStatus: async (id, active) => {
      try {
        const response = await api.patch(
          `/vehicle-settings/routes/${id}/status`,
          {
            active,
          }
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error cambiando estado de ruta predefinida ${id}:`,
          error
        );
        throw error.response?.data || error.message;
      }
    },

    getRouteClients: async () => {
      try {
        const response = await api.get(
          "/vehicle-settings/routes/filters/clients"
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo clientes para filtro:", error);
        throw error.response?.data || error.message;
      }
    },

    // ===== Proveedores Satelitales =====
    getSatelliteProviders: async () => {
      try {
        const response = await api.get("/vehicle-settings/satellite-providers");
        return response.data;
      } catch (error) {
        console.error("Error obteniendo proveedores satelitales:", error);
        throw error.response?.data || error.message;
      }
    },

    getProviderConfig: async (providerName) => {
      try {
        const response = await api.get(
          `/vehicle-settings/satellite-providers/${providerName}`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error obteniendo configuración de proveedor ${providerName}:`,
          error
        );
        throw error.response?.data || error.message;
      }
    },

    updateProviderConfig: async (providerName, configData) => {
      try {
        const response = await api.put(
          `/vehicle-settings/satellite-providers/${providerName}/config`,
          configData
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error actualizando configuración de proveedor ${providerName}:`,
          error
        );
        throw error.response?.data || error.message;
      }
    },

    updateProviderRecipients: async (providerName, recipients) => {
      try {
        const response = await api.put(
          `/vehicle-settings/satellite-providers/${providerName}/recipients`,
          {
            recipients,
          }
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error actualizando destinatarios de proveedor ${providerName}:`,
          error
        );
        throw error.response?.data || error.message;
      }
    },

    processNotifications: async (providerName) => {
      try {
        const response = await api.post(
          `/vehicle-settings/satellite-providers/${providerName}/process-notifications`
        );
        return response.data;
      } catch (error) {
        console.error(
          `Error procesando notificaciones de proveedor ${providerName}:`,
          error
        );
        throw error.response?.data || error.message;
      }
    },

    // ===== configuracion del reporte automatico =====
    getLocationReportConfig: async (date = null) => {
      try {
        // Construir la URL con parámetros de consulta
        const queryParams = new URLSearchParams();
        if (date) {
          queryParams.append("date", date);
        }

        const url = `/vehicle-settings/location-report/config${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;

        const response = await api.get(url);
        return response.data;
      } catch (error) {
        console.error(
          "Error obteniendo configuración de reporte de ubicación:",
          error
        );
        throw error.response?.data || error.message;
      }
    },

    updateReportVehicles: async (vehicles) => {
      try {
        const response = await api.put(
          "/vehicle-settings/location-report/vehicles",
          { vehicles }
        );
        return response.data;
      } catch (error) {
        console.error("Error actualizando vehículos de reporte:", error);
        throw error.response?.data || error.message;
      }
    },

    updateReportSchedules: async (schedules) => {
      try {
        const response = await api.put(
          "/vehicle-settings/location-report/schedules",
          { schedules }
        );
        return response.data;
      } catch (error) {
        console.error("Error actualizando horarios de reporte:", error);
        throw error.response?.data || error.message;
      }
    },

    processLocationReports: async () => {
      try {
        const response = await api.post(
          "/vehicle-settings/location-report/process"
        );
        return response.data;
      } catch (error) {
        console.error("Error procesando reportes de ubicación:", error);
        throw error.response?.data || error.message;
      }
    },

    updateVehicleOperator: async (vehicleId, operatorData) => {
      try {
        const response = await api.put(
          `/vehicle-settings/location-report/vehicles/${vehicleId}/operator`,
          operatorData
        );
        return response.data;
      } catch (error) {
        console.error("Error actualizando operador del vehículo:", error);
        throw error.response?.data || error.message;
      }
    },
    configureVehicleWithOperator: async (vehicleData) => {
      try {
        const response = await api.post(
          "/vehicle-settings/location-report/vehicles/configure",
          vehicleData
        );
        return response.data;
      } catch (error) {
        console.error("Error configurando vehículo con operador:", error);
        throw error.response?.data || error.message;
      }
    },
    // === reportes históricos ===
    getLocationReportRecords: async (date) => {
      try {
        const response = await api.get(
          `/vehicle-settings/location-report/records/${date}`
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo reportes de ubicación:", error);
        throw error.response?.data || error.message;
      }
    },

    getVehicleReportsByDate: async (plate, date) => {
      try {
        const response = await api.get(
          `/vehicle-settings/location-report/vehicle/${plate}/${date}`
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo reportes para el vehículo:", error);
        throw error.response?.data || error.message;
      }
    },

    createLocationReport: async (reportData) => {
      try {
        const response = await api.post(
          "/vehicle-settings/location-report/records",
          reportData
        );
        return response.data;
      } catch (error) {
        console.error("Error creando reporte de ubicación:", error);
        throw error.response?.data || error.message;
      }
    },

    updateLocationReport: async (id, reportData) => {
      try {
        const response = await api.put(
          `/vehicle-settings/location-report/records/${id}`,
          reportData
        );
        return response.data;
      } catch (error) {
        console.error("Error actualizando reporte de ubicación:", error);
        throw error.response?.data || error.message;
      }
    },

    deleteLocationReport: async (id) => {
      try {
        const response = await api.delete(
          `/vehicle-settings/location-report/records/${id}`
        );
        return response.data;
      } catch (error) {
        console.error("Error eliminando reporte de ubicación:", error);
        throw error.response?.data || error.message;
      }
    },

    getMonthlyStats: async (year, month) => {
      try {
        const response = await api.get(
          `/vehicle-settings/location-report/stats/${year}/${month}`
        );
        return response.data;
      } catch (error) {
        console.error("Error obteniendo estadísticas mensuales:", error);
        throw error.response?.data || error.message;
      }
    },

    exportDailyReportsExcel: async (date) => {
      try {
        const response = await api.get(
          `/vehicle-settings/location-report/export/${date}`,
          {
            responseType: "blob",
          }
        );
        return response.data;
      } catch (error) {
        console.error("Error exportando reportes a Excel:", error);
        throw error.response?.data || error.message;
      }
    },
  };
};
